import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { shortDate, today } from '@/lib/format';
import { useSafetyEducations, useSignTbm, useTbm } from '@/lib/queries';
import { useSite } from '@/lib/useSite';

/**
 * 안전 — TBM 서명과 교육 이수.
 *
 * <p>DOC-81 #6 이 「엔티티 0 · 법적 노출이 가장 큰 자리」로 적은 곳이다. 중대재해처벌법이
 * 요구하는 것은 결국 <b>누가 언제 무엇을 받았고 서명했는지</b>의 기록이라, 화면도 그 둘만 있다.
 *
 * <p>TBM 서명은 <b>사람마다 한 번</b>이고 멱등이다 — 현장에서 눌렸는지 확신이 안 서 다시
 * 누르는 일은 늘 있는데, 그때마다 서명이 늘면 기록이 못 쓰게 된다.
 */
export function SafetyPage() {
  const { siteId, siteName, readOnly } = useSite();
  const tbm = useTbm(siteId);
  const educations = useSafetyEducations(siteId);
  const sign = useSignTbm(siteId);

  const tbmRows = tbm.data ?? [];
  const todayTbm = tbmRows.find((row) => row.tbmDate === today());
  const eduRows = educations.data ?? [];

  return (
    <>
      <PageHeader title="안전" subtitle={siteName} />

      <h2 className="mb-2 text-ui-md font-semibold">오늘 TBM</h2>
      <QueryState
        pending={tbm.isPending}
        error={tbm.isError}
        empty={todayTbm === undefined}
        emptyText="오늘 등록된 TBM 이 없습니다."
      >
        {todayTbm !== undefined && (
          <div className="card mb-5">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="text-ui-md font-medium">{shortDate(todayTbm.tbmDate)}</div>
              <span className="badge badge-gray">
                {todayTbm.signedCount}/{todayTbm.requiredCount} 서명
              </span>
            </div>

            <dl className="mb-3 flex flex-col gap-2 text-ui-sm">
              <div>
                <dt className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>작업 내용</dt>
                <dd>{todayTbm.workSummary}</dd>
              </div>
              <div>
                <dt className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>위험요인</dt>
                <dd style={{ color: 'var(--danger)' }}>{todayTbm.hazards}</dd>
              </div>
            </dl>

            {todayTbm.signedByMe ? (
              <div className="text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>
                서명했습니다.
              </div>
            ) : (
              <button
                type="button"
                className="btn-primary w-full"
                onClick={() => sign.mutate(todayTbm.id)}
                disabled={readOnly || sign.isPending}
              >
                {sign.isPending ? '보내는 중입니다…' : '확인하고 서명'}
              </button>
            )}
          </div>
        )}
      </QueryState>

      <h2 className="mb-2 text-ui-md font-semibold">교육 이수</h2>
      <QueryState
        pending={educations.isPending}
        error={educations.isError}
        empty={eduRows.length === 0}
        emptyText="이수 기록이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {eduRows.map((row) => (
            <li key={row.id} className="card flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-ui-md">{row.title}</div>
                <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {row.workerName} · {shortDate(row.educatedOn)} · {row.hours}시간
                </div>
              </div>
              <span className={`badge shrink-0 ${row.certificateKey === null ? 'badge-red' : 'badge-gray'}`}>
                {row.certificateKey === null ? '이수증 없음' : row.educationType}
              </span>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
